import { createFileRoute, useNavigate } from '@tanstack/react-router';
import * as z from 'zod';
import { LegacyReviewsPage } from '@/pages/legacy-reviews';
import { requireAuth } from '../require-auth';

/**
 * URL state for the legacy Отзывы screen — a 1-to-1 port of the old panel's
 * `/reviews` page. The URL is the single source of truth for `page`/`limit`,
 * as the original's `CustomPagination` kept them in the query string.
 *
 * There is no new-design counterpart: this route always renders the legacy
 * page, in both modes. It is reached from the legacy dashboard's Reviews
 * card footer.
 *
 * The default limit is 18 — the old page's `limitList` was
 * `[6, 18, 24, 30, 36, 42]` and `CustomPagination` defaulted to the second
 * entry. Both are defaulted (not merely `.catch()`ed) so navigating here
 * from the dashboard tile doesn't have to supply them.
 */
const deliveryReviewsSearchSchema = z.object({
  page: z.number().int().min(1).default(1).catch(1),
  limit: z.number().int().min(1).default(18).catch(18),
});

export const Route = createFileRoute('/delivery/reviews')({
  // Auth guard — see `require-auth.ts`. Runs before the component mounts,
  // so no query fires without a session.
  beforeLoad: requireAuth,
  validateSearch: deliveryReviewsSearchSchema,
  component: DeliveryReviewsRoute,
});

function DeliveryReviewsRoute() {
  const { page, limit } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  return (
    <LegacyReviewsPage
      page={page}
      limit={limit}
      onPageChange={(next) => {
        void navigate({ search: (prev) => ({ ...prev, page: next }) });
      }}
      onLimitChange={(next) => {
        // A new page size restarts paging — page 4 of 18-per-page points
        // nowhere once the pages are 42 rows tall.
        void navigate({ search: (prev) => ({ ...prev, limit: next, page: 1 }) });
      }}
    />
  );
}
